import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Clock, LifeBuoy, Users } from "lucide-react";
import { ArticleBody } from "@/components/kb/ArticleBody";
import { ArticleFeedbackPanel } from "@/components/kb/ArticleFeedbackPanel";
import { useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import { ApiError } from "@/lib/api";
import {
  articleHeadings,
  articlePlainText,
  parseArticle,
  readingMinutes,
} from "@/lib/article-markdown";
import { CONTENT_TYPE_LABELS, getArticle, type ArticleDetail } from "@/lib/knowledge";

export const Route = createFileRoute("/kb/$collection/$slug")({ component: ArticlePage });

/**
 * One article, laid out the way Microsoft Learn lays one out: breadcrumb, the article, and an
 * "In this article" rail built from its own headings.
 *
 * ⚠️ **The body is MARKDOWN and is drawn as ELEMENTS.** `parseArticle` produces a tree of known
 * node kinds and `ArticleBody` renders it; there is no HTML string at any point, which is what
 * satisfies the ban on `dangerouslySetInnerHTML` structurally rather than by sanitising. An
 * author who pastes `<script>` gets five visible words — asserted in the parser's own suite.
 *
 * ⚠️ **The rail is built from the parsed document, not typed by the author**, so it cannot
 * describe a page that no longer exists.
 */
function ArticlePage() {
  const { collection, slug } = Route.useParams();
  const zone = useZone();

  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(null);

    void getArticle(collection, slug)
      .then((data) => {
        if (live) setArticle(data);
      })
      .catch((error: unknown) => {
        if (!live) return;
        // ⚠️ "Not found" and "not yours to read" are one answer, deliberately — the server
        // answers 404 to both, so this screen can never become a way to learn what exists.
        setFailed(
          error instanceof ApiError && error.status === 404
            ? "That article could not be found."
            : "This article could not be loaded.",
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [collection, slug]);

  // Parsed once per body rather than on every render — a long article is a real amount of work
  // and the tree is what three separate things below read.
  const blocks = useMemo(() => (article === null ? [] : parseArticle(article.body)), [article]);

  const headings = useMemo(() => articleHeadings(blocks), [blocks]);
  const minutes = useMemo(() => readingMinutes(articlePlainText(blocks)), [blocks]);

  if (loading) {
    return <p className="p-6 text-[13px] text-muted-foreground">Loading…</p>;
  }

  if (failed !== null || article === null) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-[13px] font-medium">{failed ?? "That article could not be found."}</p>
          <Link
            to="/kb"
            className="mt-2 inline-block text-[12px] text-primary underline underline-offset-2"
          >
            Back to the knowledge base
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground"
      >
        <Link to="/kb" className="hover:text-foreground">
          Knowledge base
        </Link>
        <ChevronRight className="size-3" aria-hidden />
        <Link
          to="/kb/$collection"
          params={{ collection: article.collectionSlug }}
          className="hover:text-foreground"
        >
          {article.collectionName}
        </Link>
        <ChevronRight className="size-3" aria-hidden />
        <span className="text-foreground">{article.title}</span>
      </nav>

      {/* The rail is a sibling rather than a wrapper, so on a narrow screen the article comes
          first and the contents list simply is not drawn — a table of contents above a page
          you can scroll is furniture on a phone. */}
      <div className="gap-8 lg:grid lg:grid-cols-[minmax(0,1fr)_220px]">
        <article className="min-w-0">
          <header className="space-y-3 border-b border-border pb-5">
            <h1 className="text-2xl font-semibold tracking-tight">{article.title}</h1>

            {article.summary ? (
              <p className="text-[14px] text-muted-foreground">{article.summary}</p>
            ) : null}

            <dl className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-muted-foreground">
              <div className="rounded border border-border px-1.5 py-0.5 text-foreground">
                {CONTENT_TYPE_LABELS[article.contentType]}
              </div>

              {article.appliesTo ? (
                <div>
                  <dt className="sr-only">Applies to</dt>
                  <dd>{article.appliesTo}</dd>
                </div>
              ) : null}

              <div className="flex items-center gap-1">
                <Clock className="size-3" aria-hidden />
                <dt className="sr-only">Reading time</dt>
                <dd>{minutes} min read</dd>
              </div>

              <div>
                <dt className="sr-only">Last updated</dt>
                {/* ⚠️ The reader's own zone, never the browser's — CI guards this. */}
                <dd>Updated {formatDate(article.updatedAtUtc, zone)}</dd>
              </div>

              <div className="flex items-center gap-1">
                <Users className="size-3" aria-hidden />
                <dt className="sr-only">Maintained by</dt>
                {/* ⚠️ The owning TEAM, never the author's name. Who wrote a how-to guide is not
                    a fact the reader needs, and a name on every article is a person-descriptor
                    on a page every employee can open. */}
                <dd>Maintained by {article.owningTeamName}</dd>
              </div>
            </dl>
          </header>

          <div className="py-6">
            <ArticleBody blocks={blocks} />
          </div>

          {/* Was this helpful? The only measure of an article that comes from the person it was
              for — and it records nobody. See the component. */}
          <ArticleFeedbackPanel collection={article.collectionSlug} slug={article.slug} />

          {/* ⚠️ Every article offers a way out for the person it did not help. A self-service
              article that dead-ends is how somebody who has read the whole thing ends up
              phoning — the outcome this feature exists to avoid, reached from the far end. */}
          <footer className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 p-4">
            <LifeBuoy className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <p className="flex-1 text-[13px] text-muted-foreground">
              Still stuck? Raise a request and the service desk will pick it up.
            </p>
            <Link
              to="/new"
              className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground"
            >
              Raise a request
            </Link>
          </footer>
        </article>

        {headings.length > 1 ? (
          <aside className="hidden lg:block">
            <nav aria-labelledby="in-this-article" className="sticky top-20">
              <p
                id="in-this-article"
                className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                In this article
              </p>
              <ul className="space-y-1.5 border-l border-border">
                {headings.map((heading) => (
                  <li key={heading.id}>
                    <a
                      href={`#${heading.id}`}
                      className={
                        heading.level === 3
                          ? "-ml-px block border-l border-transparent py-0.5 pl-6 text-[12px] text-muted-foreground hover:border-foreground hover:text-foreground"
                          : "-ml-px block border-l border-transparent py-0.5 pl-3 text-[12px] text-muted-foreground hover:border-foreground hover:text-foreground"
                      }
                    >
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
